/* eslint-disable jsx-a11y/no-autofocus */
import { FormField, Input } from '@wix/design-system';
import type { FocusEvent } from 'react';
import React, { useEffect, useState } from 'react';

import type { TFunction } from './../../../ricos-types/commonTypes';
import { useBiNext } from './../../bi-context-provider/bi-context-provider';
import type { NamedLink } from './../../link/types';
import type { Timeout } from './../../types/globals';

import { LINK_MODAL_ID } from '../constants';
import { URL_INPUT } from '../data-hooks/data-hooks';
import type { SubmitError } from './types';

type Props = {
  autoFocus: boolean;
  link: NamedLink;
  onChangeUrl: (url: string | undefined) => void;
  onValidateUrl: (url: string) => void;
  submitError: SubmitError | null;
  t: TFunction;
};

export const WebAddress: React.FC<Props> = ({
  autoFocus,
  link,
  onChangeUrl,
  onValidateUrl,
  submitError,
  t,
}) => {
  const logBi = useBiNext({ pluginId: 'link', modalId: LINK_MODAL_ID });
  const [validateTimeout, setValidateTimeout] = useState<Timeout | undefined>(undefined);

  const onBlur = (evt: FocusEvent<HTMLInputElement, Element>) => {
    onValidateUrl(evt.target.value);
  };

  const onChange = (value: string) => {
    onChangeUrl(value);
    setValidateTimeout(validateTimeout => {
      clearTimeout(validateTimeout);
      return setTimeout(() => onValidateUrl(value), 2_000);
    });
  };

  useEffect(() => () => clearTimeout(validateTimeout), [validateTimeout]);

  const invalidUrl = submitError === 'invalid-url';

  return (
    <>
      <FormField
        label={t('LinkSettings_WebAddress_Label')}
        labelPlacement="top"
        status={invalidUrl ? 'error' : undefined}
        statusMessage={invalidUrl ? t('LinkSettings_WebAddress_InvalidLink') : undefined}
      >
        <Input
          autoFocus={autoFocus}
          dataHook={URL_INPUT}
          onBlur={onBlur}
          onChange={evt => {
            logBi({ dataHook: URL_INPUT, value: evt.target.value });
            onChange(evt.target.value);
          }}
          placeholder={t('LinkSettings_WebAddress_Placeholder')}
          size="small"
          value={link.url}
        />
      </FormField>
    </>
  );
};
